import React, { useEffect, useRef, useState } from 'react'
import MuiDrawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import { useNavigate, useLocation } from 'react-router-dom'
import { Navigation, NavigationPageItem } from 'types/navigation'
import { mapPagesToDrawer } from 'router/mapPagesToDrawer'
import { hubForPath } from 'router/hubNavigation'
import { useTheme, alpha } from '@mui/material/styles'
import Settings from '@mui/icons-material/Settings'
import Close from '@mui/icons-material/Close'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { useNavDensity } from 'contexts/NavDensityContext'
import { useSidebarPins } from 'hooks/navigation/useSidebarPins'
import { collectPinnablePages, isPinnedRoute } from 'router/sidebarPins'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useAuth } from 'hooks/auth/useAuth'
import Typography from '@mui/material/Typography'
import { ThemeModeToggle } from 'components/ThemeModeToggle'
import { CoreAccountDrawerSection } from './CoreAccountDrawerSection'
import { APP_RELEASE_LABEL, APP_VERSION } from '@/constants/appVersion'
import {
  APPBAR_HEIGHT_DESKTOP,
  BRAND_NAVY,
  BRAND_NAVY_DARK,
  BRAND_RED,
  CHROME_GAP,
  CHROME_RADIUS,
  DRAWER_COMFORTABLE,
  DRAWER_COMPACT,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from 'core/theme.tokens'

interface CoreDrawerProps {
  mobileOpen: boolean
  onMobileClose: () => void
  navigationItems: Navigation
  setMobileOpen: (open: boolean) => void
  hubMode?: boolean
}

export const CoreDrawer: React.FC<CoreDrawerProps> = ({
  mobileOpen,
  onMobileClose,
  navigationItems,
  setMobileOpen,
  hubMode = false,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { userInfo } = useAuth()
  const { pins, togglePin } = useSidebarPins()
  const { density, setDensity } = useNavDensity()
  // Same phone-chrome signal as CoreLayout / CoreAppBar (not width-only),
  // so the hamburger can open the temporary drawer on iPad / desktop-site phones.
  const isMobile = useIsMobile()
  const comfortable = isMobile || density === 'comfortable'
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: string]: boolean
  }>({})
  const navigationRef = useRef(navigationItems)
  navigationRef.current = navigationItems
  const pinsRef = useRef(pins)
  pinsRef.current = pins
  const pinKey = pins.join('\n')

  // Keep the hub that owns the current route expanded (accordion; don't fight a user close).
  // On Dashboard, collapse all hubs so the rail stays quiet.
  // A pinned route stays on the pin only — don't open or select its hub.
  // Depend on the path — the pages array is a new reference every parent render,
  // and re-running here would undo a section the user just opened from the compact rail.
  useEffect(() => {
    if (!hubMode) return
    const path = location.pathname
    const isDashboard = path === '/admin' || path === '/admin/'
    if (isDashboard) {
      setOpenDropdowns({})
      return
    }
    const pages = navigationRef.current
    const hub = hubForPath(pages, path)
    if (!hub) return
    const key = `hub-${hub}`
    if (isPinnedRoute(collectPinnablePages(pages), pinsRef.current, path)) {
      setOpenDropdowns(prev => {
        if (!prev[key]) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
      return
    }
    setOpenDropdowns(prev => {
      if (Object.prototype.hasOwnProperty.call(prev, key) && prev[key] === false) {
        return prev
      }
      return { [key]: true }
    })
  }, [hubMode, location.pathname, pinKey])

  const expandDropdown = (segment: string) => {
    setOpenDropdowns({ [segment]: true })
  }

  const drawerWidth = comfortable ? DRAWER_COMFORTABLE : DRAWER_COMPACT

  const findNavItem = (items: Navigation, path: string): NavigationPageItem | null => {
    for (const item of items) {
      if ('segment' in item && item.segment === path) {
        return item as NavigationPageItem
      }
      if ('children' in item && item.children) {
        const found = findNavItem(item.children, path)
        if (found) return found
      }
    }
    return null
  }

  const handleNavigation = (path?: string) => {
    if (!path) {
      throw new Error('Something weird happened.. path is undefined')
    }
    findNavItem(navigationItems, path)
    navigate(`/${path}`)
    if (isMobile && mobileOpen) {
      setMobileOpen(false)
    }
  }

  const handleDropdownToggle = (segment: string) => {
    setOpenDropdowns(prev => {
      const willOpen = !prev[segment]
      if (!willOpen) {
        return { ...prev, [segment]: false }
      }
      // Accordion: only one section open at a time
      return { [segment]: true }
    })
  }

  const itemSx = {
    minHeight: { xs: 48, sm: 40 },
    justifyContent: comfortable ? 'initial' : 'center',
    px: comfortable ? 2 : 0,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  } as const

  const iconSx = {
    minWidth: 0,
    mr: comfortable ? 2 : 0,
    justifyContent: 'center',
  } as const

  const paperSx = {
    width: isMobile ? '100vw' : drawerWidth,
    maxWidth: isMobile ? '100vw' : drawerWidth,
    boxSizing: 'border-box' as const,
    top: isMobile
      ? 0
      : `calc(${CHROME_GAP}px + ${SAFE_AREA_TOP} + ${APPBAR_HEIGHT_DESKTOP}px + ${CHROME_GAP}px)`,
    left: isMobile ? 0 : CHROME_GAP,
    right: isMobile ? 0 : 'auto',
    bottom: isMobile ? 0 : CHROME_GAP,
    height: isMobile ? '100dvh' : 'auto',
    maxHeight: isMobile ? '100dvh' : 'none',
    borderRadius: isMobile ? 0 : `${CHROME_RADIUS}px`,
    border: isMobile ? 'none' : '1px solid',
    borderColor: isMobile
      ? 'transparent'
      : alpha(theme.palette.mode === 'dark' ? '#ffffff' : BRAND_NAVY, 0.12),
    overflow: 'hidden',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'column',
    pt: isMobile ? SAFE_AREA_TOP : 0,
    // Full-screen overlay covers the bottom bar; do not reserve space for it.
    pb: isMobile ? SAFE_AREA_BOTTOM : 0,
    backgroundColor: alpha(BRAND_NAVY_DARK, theme.palette.mode === 'dark' ? 0.92 : 0.88),
    backgroundImage: `linear-gradient(
      165deg,
      ${alpha(BRAND_NAVY, 0.97)} 0%,
      ${alpha(BRAND_NAVY, 0.9)} 36%,
      ${alpha(BRAND_RED, 0.62)} 70%,
      ${alpha(BRAND_NAVY_DARK, 0.96)} 100%
    )`,
    backgroundBlendMode: 'normal',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    color: '#F4F1EB',
    boxShadow: isMobile ? 'none' : `0 12px 32px ${alpha(BRAND_NAVY, 0.28)}`,
    '& .MuiListItemIcon-root': {
      color: 'inherit',
    },
    '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
      color: 'inherit',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiDivider-root': {
      borderColor: alpha('#F4F1EB', 0.2),
    },
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }

  const drawerBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {isMobile ? (
        <List sx={{ py: 0.5, flexShrink: 0 }}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton onClick={onMobileClose} aria-label='close drawer' sx={itemSx}>
              <ListItemIcon sx={iconSx}>
                <Close />
              </ListItemIcon>
              <ListItemText primary='Close' />
            </ListItemButton>
          </ListItem>
        </List>
      ) : null}
      {isMobile ? <Divider /> : null}
      <List
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 0,
          px: 0,
          minWidth: 0,
        }}
      >
        {mapPagesToDrawer(navigationItems, {
          theme,
          open: comfortable,
          setOpen: next => {
            if (isMobile) {
              setMobileOpen(next)
              return
            }
            setDensity(next ? 'comfortable' : 'compact')
          },
          location,
          openDropdowns,
          handleNavigation,
          handleDropdownToggle,
          expandDropdown,
          hubMode,
          pins,
          onTogglePin: togglePin,
        })}
      </List>
      <Divider />
      <Box
        sx={{
          px: comfortable ? 1 : 0,
          pt: 0,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: comfortable ? 'stretch' : 'center',
          gap: 0,
          flexShrink: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {isMobile ? (
          <CoreAccountDrawerSection
            onNavigate={() => {
              setMobileOpen(false)
            }}
          />
        ) : comfortable ? (
          <Typography
            variant='body2'
            noWrap
            sx={{ px: 1, color: alpha('#F4F1EB', 0.78), maxWidth: '100%' }}
          >
            {[userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ') ||
              userInfo?.email}
          </Typography>
        ) : null}
        <List sx={{ py: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
            <ThemeModeToggle
              variant='nav'
              comfortable={comfortable}
              itemSx={itemSx}
              iconSx={iconSx}
            />
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
            <Tooltip title={comfortable ? '' : 'Settings'} placement='right'>
              <ListItemButton
                onClick={() => {
                  navigate('/account-settings')
                  if (isMobile) setMobileOpen(false)
                }}
                aria-label='Settings'
                sx={{ ...itemSx, maxWidth: '100%' }}
              >
                <ListItemIcon sx={iconSx}>
                  <Settings />
                </ListItemIcon>
                {comfortable ? (
                  <ListItemText
                    primary='Settings'
                    primaryTypographyProps={{ noWrap: true }}
                    sx={{ minWidth: 0 }}
                  />
                ) : null}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          {isMobile ? null : (
            <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
              <Tooltip title={comfortable ? '' : 'Open sidebar'} placement='right'>
                <ListItemButton
                  onClick={() => setDensity(density === 'comfortable' ? 'compact' : 'comfortable')}
                  aria-label={density === 'comfortable' ? 'Close sidebar' : 'Open sidebar'}
                  sx={{ ...itemSx, maxWidth: '100%' }}
                >
                  <ListItemIcon sx={iconSx}>
                    {density === 'comfortable' ? <ChevronLeft /> : <ChevronRight />}
                  </ListItemIcon>
                  {comfortable ? (
                    <ListItemText
                      primary='Close sidebar'
                      primaryTypographyProps={{ noWrap: true }}
                      sx={{ minWidth: 0 }}
                    />
                  ) : null}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}
        </List>
        {comfortable ? (
          <Typography
            variant='caption'
            noWrap
            sx={{
              px: 2,
              py: 0.5,
              color: alpha('#F4F1EB', 0.55),
              textAlign: 'center',
              lineHeight: 1.2,
              maxWidth: '100%',
            }}
          >
            {APP_RELEASE_LABEL}
          </Typography>
        ) : (
          <Tooltip title={APP_RELEASE_LABEL} placement='right'>
            <Typography
              variant='caption'
              sx={{
                color: alpha('#F4F1EB', 0.55),
                fontSize: 10,
                lineHeight: 1.2,
                width: '100%',
                textAlign: 'center',
              }}
            >
              {`v${APP_VERSION}`}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  )

  if (isMobile) {
    return (
      <MuiDrawer
        variant='temporary'
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          // Do not hide at sm+: phone chrome can be wider than the sm breakpoint
          // (iPad, “Request Desktop Website”) while still using the hamburger.
          display: 'block',
          // keepMounted leaves the full-screen Modal root in the DOM after close.
          // If the slide exit never finishes, that root still sits over the page
          // and swallows every click. Drop pointer events as soon as it closes.
          pointerEvents: mobileOpen ? 'auto' : 'none',
          '& .MuiDrawer-paper': {
            ...paperSx,
            pointerEvents: mobileOpen ? 'auto' : 'none',
          },
        }}
      >
        {drawerBody}
      </MuiDrawer>
    )
  }

  return (
    <MuiDrawer
      variant='permanent'
      open
      sx={{
        width: 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': paperSx,
      }}
    >
      {drawerBody}
    </MuiDrawer>
  )
}
